import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Form,
  Input,
  Button,
  Select,
  message,
  Spin,
  Card,
  Typography,
  Space,
  Divider,
  Alert
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Lead } from '../services/api';
import apiService from '../services/api';
import { VodafoneTemplate, VodafoneField, VodafoneDocument } from '../services/api';
import { mergeTemplateFieldValues } from '../utils/fieldMapping';

const { Step } = Steps;
const { Option } = Select;
const { Title, Text } = Typography;

interface VodafoneDocumentWizardProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;  // Callback when documents are saved
  lead: Lead | null;
}



const VodafoneDocumentWizard: React.FC<VodafoneDocumentWizardProps> = ({ open, onClose, onSave, lead }) => {
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<VodafoneTemplate[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [allFields, setAllFields] = useState<Record<string, VodafoneField[]>>({});
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [previewData, setPreviewData] = useState<Record<string, string>>({});
  const [generatedDocs, setGeneratedDocs] = useState<VodafoneDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [documentPages, setDocumentPages] = useState<Record<string, any[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [documentProgress, setDocumentProgress] = useState<Record<string, number>>({});

  // Re-merge when templates/fields or lead contact data change — preserves saved + in-session values
  useEffect(() => {
    if (!open || !lead || Object.keys(allFields).length === 0) return;
    const flat = Object.values(allFields).flat();
    setFieldValues((prev) => mergeTemplateFieldValues(flat, lead, prev));
  }, [open, lead?.id, lead?.name, lead?.dif, lead?.address, lead?.phone, lead?.email, lead?.city, lead?.postal_code, allFields]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setSelectedTemplates([]);
      setCurrentDocumentIndex(0);
      setAllFields({});
      setFieldValues({});
      setPreviewData({});
      setGeneratedDocs([]);
      setError(null);
      setDocumentPages({});
      setCurrentPage(1);
      setDocumentProgress({});
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    setLoadingMessage('A carregar modelos de documento...');
    try {
      // Only show Vodafone templates that exist in the backend Vodafone field mapping config
      const data = await apiService.getVodafoneTemplates();
      // Filter templates to only those present in the Vodafone field mapping config
      const vodafoneTemplateNames = [
        'Contrato de Adesão',
        'Formulario Desativação Apoiada',
        'Alteração de titularidade e transferência de contrato de_fidelização',
        'Pedido de portabilidade e denúncia de contrato',
        'Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes'
      ];
      setTemplates(data.filter((tpl: any) => vodafoneTemplateNames.includes(tpl.name)));
    } catch (e) {
      setError('Erro ao carregar os modelos de documento.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const loadDocumentPages = async (templateNames: string[]) => {
    setLoading(true);
    setLoadingMessage('A carregar estrutura das páginas...');
    try {
      const pagesMap: Record<string, any[]> = {};
      for (const templateName of templateNames) {
        // Always use backend-defined pagination for Vodafone documents
        const response = await apiService.getVodafoneTemplateFields(templateName);
        // Find the correct page config from the backend mapping (pages array)
        let pageConfig = null;
        if (templateName === 'Contrato de Adesão') {
          // Hardcode the correct pages for Contrato de Adesão as per backend config
          pageConfig = [
            { page_number: 1, page_name: 'DADOS DO CLIENTE', field_names: [
              'Nome Completo', 'Contribuinte', 'Telefone Contacto', 'Morada', 'Código Postal 4', 'Código Postal 3', 'Localidade', 'Email'] },
            { page_number: 2, page_name: 'PACOTE / SERVIÇOS CONTRATADOS', field_names: [
              'Pacote', 'Internet FIxa'] },
            { page_number: 3, page_name: 'TARIFÁRIOS MÓVEIS', field_names: [
              'Telemóvel 1', 'Telemóvel 2', 'Telemóvel 3', 'Telemóvel 4', 'Internet Móvel 1', 'Internet Móvel 2', 'Tarifário Internet 1', 'Tarifário Internet 2', 'Tarifário 1', 'Tarifário 2', 'Tarifário 3', 'Tarifário 4'] },
            { page_number: 4, page_name: 'FATURA', field_names: [
              'Fatura Detalhada'] },
            { page_number: 5, page_name: 'AUTORIZAÇÃO DE PAGAMENTO POR DÉBITO DIRETO', field_names: [
              'Nome Completo Autorização', 'IBAN'] },
            { page_number: 6, page_name: 'DADOS DE VENDA', field_names: [
              'Dia', 'Mês', 'Ano', 'SFID', 'Nome / Assinatura Comercial'] }
          ];
        }
        // Add hardcoded pageConfig for Formulario Desativação Apoiada
        else if (templateName === 'Formulario Desativação Apoiada') {
          pageConfig = [
            { page_number: 1, page_name: 'INFORMAÇÃO DO CLIENTE', field_names: [
              'Nome Completo', 'Morada Completa', 'Código Postal 4', 'Código Postal 3'] },
            { page_number: 2, page_name: 'RESCISÃO DE CONTRATO', field_names: [
              'VRef cliente N', 'Operadora', 'Cidade', 'dia', 'mes', 'ano'] }
          ];
        }
        // Add hardcoded pageConfig for Pedido de portabilidade e denúncia de contrato
        else if (templateName === 'Pedido de portabilidade e denúncia de contrato') {
          pageConfig = [
            { page_number: 1, page_name: 'DADOS DO CLIENTE NO OPERADOR DE ORIGEM', field_names: [
              'Nome Completo', 'NIF'] },
            { page_number: 2, page_name: 'IDENTIFICAÇÃO DOS NÚMEROS A PORTAR PARA A VODAFONE', field_names: [
              'N de TelemóvelRow1', 'Código de Validação de Portabilidade CVPRow1', 'N Cartão SIMRow1', 
              'N de TelemóvelRow2', 'Código de Validação de Portabilidade CVPRow2', 'N Cartão SIMRow2',
              'N de TelemóvelRow3', 'Código de Validação de Portabilidade CVPRow3', 'N Cartão SIMRow3',
              'N de TelemóvelRow4', 'Código de Validação de Portabilidade CVPRow4', 'N Cartão SIMRow4',
              'N de TelemóvelRow5', 'Código de Validação de Portabilidade CVPRow5', 'N Cartão SIMRow5'] },
            { page_number: 3, page_name: 'PARTE FINAL', field_names: [
              'SFID', 'Nome  Assinatura Comercial', 'Dia', 'Mês', 'Ano', 'Hora', 'Minuto'] }
          ];
        }
        // Add hardcoded pageConfig for Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes
        else if (templateName === 'Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes') {
          pageConfig = [
            { page_number: 1, page_name: 'DADOS DO CLIENTE NO OPERADOR DE ORIGEM', field_names: [
              'Nome Completo', 'NIF', 'Telemóvel de Contacto'] },
            { page_number: 2, page_name: 'IDENTIFICAÇÃO DO NÚMERO A PORTAR PARA A VODAFONE', field_names: [
              'Telefone Fixo a Portar', 'CVP', 'Dia', 'Mês', 'Ano', 'Hora', 'Minuto', 'SFID', 'Nome  Assinatura Comercial'] }
          ];
        }
        // Add hardcoded pageConfig for Alteração de titularidade e transferência de contrato de_fidelização
        else if (templateName === 'Alteração de titularidade e transferência de contrato de_fidelização') {
          pageConfig = [
            { page_number: 1, page_name: 'Declaração de alteração de titularidade', field_names: [
              'Serviço', 'Nome do Cliente', 'NIF', 'Número', 'Novo Titular', 'NIF Novo Titular'] },
            { page_number: 2, page_name: 'Declaração de transferência de contrato de fidelização', field_names: [
              'Serviço nº', 'Fim do contrato', 'Valor da denúncia antecipada'] }
          ];
        }
    
        if (pageConfig) {
          pagesMap[templateName] = pageConfig;
        } else {
          // Fallback: single page with all fields
          const fields = response || [];
          pagesMap[templateName] = [{
            page_number: 1,
            page_name: 'Campos',
            page_description: 'Todos os campos do documento',
            field_names: fields.map((f: any) => f.name)
          }];
        }
      }
      setDocumentPages(pagesMap);
    } catch (e) {
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const loadFields = async (templateNames: string[]): Promise<Record<string, VodafoneField[]>> => {
    setLoading(true);
    setLoadingMessage('A carregar campos do documento...');
    try {
      const fieldsMap: Record<string, VodafoneField[]> = {};
      for (const templateName of templateNames) {
        const data = await apiService.getVodafoneTemplateFields(templateName);
        // Use the field name and label as provided by the backend (no prefixing)
        fieldsMap[templateName] = data;
      }
      setAllFields(fieldsMap);
      return fieldsMap;
    } catch (e) {
      setError('Erro ao carregar os campos do documento.');
      return {};
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleTemplateSelect = (values: string[]) => {
    setSelectedTemplates(values);
    setFieldValues({});
    setAllFields({});
    setPreviewData({});
    setGeneratedDocs([]);
    // Do NOT load fields here
  };

  const handleNextStep = async () => {
    if (step === 0 && selectedTemplates.length > 0) {
      setLoading(true);
      setLoadingMessage('A preparar documentos...');
      const fieldsMap = await loadFields(selectedTemplates);
      await loadDocumentPages(selectedTemplates);

      let savedFv: Record<string, any> = {};
      const progressMap: Record<string, number> = {};

      if (lead?.id) {
        try {
          const [fieldValuesResponse, progressResponse] = await Promise.all([
            apiService.getFieldValues(lead.id),
            apiService.getDocumentProgress(lead.id),
          ]);
          if (fieldValuesResponse.success && fieldValuesResponse.field_values) {
            savedFv = fieldValuesResponse.field_values;
          }
          if (progressResponse.success && progressResponse.progress) {
            Object.entries(progressResponse.progress).forEach(([docType, progress]: [string, any]) => {
              progressMap[docType] = progress.current_page;
            });
          }
        } catch {
          // keep defaults
        }
      }

      setDocumentProgress(progressMap);

      const flat = Object.values(fieldsMap).flat();
      const merged = mergeTemplateFieldValues(flat, lead, savedFv);
      setFieldValues(merged);

      const currentTemplate = selectedTemplates[0];
      setCurrentPage(progressMap[currentTemplate] || 1);

      setLoading(false);
      setLoadingMessage('');
      setStep(1);
    }
  };

  const handlePreviousStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleFieldChange = (name: string, value: any) => {
    
    setFieldValues((prev) => {
      const newValues = { ...prev, [name]: value };
      
      // Auto-fill related fields across all templates
      const currentTemplate = getCurrentTemplate();
      const allTemplateFields = Object.values(allFields).flat();
      
      allTemplateFields.forEach(field => {
        if (field.related_field === name && field.name !== name) {
          // Special handling for postal code splitting
          if (name === 'codigo_postal' && value) {
            const parts = value.split('-');
            if (parts.length === 2) {
              if (field.name === 'Código Postal 4') {
                newValues[field.name] = parts[0];
              } else if (field.name === 'Código Postal 3') {
                newValues[field.name] = parts[1];
              }
            }
          } else {
            newValues[field.name] = value;
          }
        }
      });
      
      
      
      
      return newValues;
    });
  };

  const getFieldValidationError = (field: any, value: any) => {
    if (field.validation && value) {
      const pattern = new RegExp(field.validation.pattern);
      if (!pattern.test(value)) {
        return field.validation.message || 'Formato inválido';
      }
    }
    return null;
  };

  const getCurrentTemplate = () => {
    return selectedTemplates[currentDocumentIndex];
  };

  const getCurrentFields = () => {
    const currentTemplate = getCurrentTemplate();
    return currentTemplate ? allFields[currentTemplate] || [] : [];
  };

  const saveCurrentProgress = async () => {
    if (!lead) return;
    
    try {
      // Save field values
      await apiService.saveFieldValues(lead.id, fieldValues);
      
      // Save document progress
      const currentTemplate = getCurrentTemplate();
      if (currentTemplate) {
        await apiService.saveDocumentProgress(lead.id, currentTemplate, currentPage);
      }
    } catch (e) {
    }
  };

  const handleNextDocument = async () => {
    // Validate current fields before proceeding
    const currentTemplate = getCurrentTemplate();
    const currentFields = getCurrentFields();
    const validationErrors: string[] = [];
    
    // Check validation for each field
    currentFields.forEach(field => {
      const value = fieldValues[field.name];
      if (field.validation && value) {
        const pattern = new RegExp(field.validation.pattern);
        if (!pattern.test(value)) {
          validationErrors.push(`${field.label || field.name}: ${field.validation.message || 'Formato inválido'}`);
        }
      }
    });
    
    if (validationErrors.length > 0) {
      // Show validation errors and prevent proceeding
      message.error('Por favor, corrija os seguintes erros antes de continuar:');
      validationErrors.forEach(error => {
        message.error(error);
      });
      return;
    }
    
    setLoading(true);
    setLoadingMessage('A processar documento...');
    
    try {
      // Save current progress before moving to next document
      await saveCurrentProgress();
      
      if (currentDocumentIndex < selectedTemplates.length - 1) {
        setCurrentDocumentIndex(currentDocumentIndex + 1);
        setCurrentPage(1); // Reset to first page for new document
      } else {
        // All documents completed, move to preview step
        setLoadingMessage('A preparar revisão...');
        setStep(2);
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handlePreviousDocument = async () => {
    // Save current progress before moving to previous document
    await saveCurrentProgress();
    
    if (currentDocumentIndex > 0) {
      setCurrentDocumentIndex(currentDocumentIndex - 1);
      // Set page based on saved progress or default to 1
      const previousTemplate = selectedTemplates[currentDocumentIndex - 1];
      const savedPage = documentProgress[previousTemplate] || 1;
      setCurrentPage(savedPage);
    } else {
      setStep(0);
    }
  };

  const handlePreview = async () => {
    if (selectedTemplates.length === 0) return;
    
    // Validate all fields across all templates before preview
    const validationErrors: string[] = [];
    
    for (const templateName of selectedTemplates) {
      const templateFields = allFields[templateName] || [];
      templateFields.forEach(field => {
        const value = fieldValues[field.name];
        if (field.validation && value) {
          const pattern = new RegExp(field.validation.pattern);
          if (!pattern.test(value)) {
            validationErrors.push(`${templateName} - ${field.label || field.name}: ${field.validation.message || 'Formato inválido'}`);
          }
        }
      });
    }
    
    if (validationErrors.length > 0) {
      // Show validation errors and prevent proceeding
      message.error('Por favor, corrija os seguintes erros antes de gerar a pré-visualização:');
      validationErrors.forEach(error => {
        message.error(error);
      });
      return;
    }
    
    setLoading(true);
    setLoadingMessage('A gerar pré-visualização dos documentos...');
    setError(null);
    try {
      const previewData: Record<string, string> = {};
      // Preview all selected documents
      for (const templateName of selectedTemplates) {
        setLoadingMessage(`A gerar pré-visualização: ${templateName}...`);
        const templateFieldValues: Record<string, any> = {};
        // Extract field values for this specific template
        // IMPORTANT: Include ALL fields from the template, even if they're undefined
        // Set undefined fields to empty string so they pass "field exists" validation
        // Required fields that are empty will still fail "cannot be empty" validation (expected)
        const templateFields = allFields[templateName] || [];
        templateFields.forEach(field => {
          const value = fieldValues[field.name];
          // Always include the field - use empty string if undefined to pass "field exists" check
          templateFieldValues[field.name] = value !== undefined && value !== null ? value : '';
        });
        
        const pdfBase64 = await apiService.previewVodafoneDocument(templateName, templateFieldValues, lead?.id);
        previewData[templateName] = pdfBase64;
      }
      setPreviewData(previewData);
      setStep(3);
    } catch (e) {
      setError('Erro ao gerar pré-visualização.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleGenerate = async () => {
    if (!lead || selectedTemplates.length === 0) return;
    setLoading(true);
    setLoadingMessage('A guardar documentos...');
    setError(null);
    try {
      const newGeneratedDocs: VodafoneDocument[] = [];
      // Generate all selected documents
      for (const templateName of selectedTemplates) {
        setLoadingMessage(`A guardar documento: ${templateName}...`);
        const templateFieldValues: Record<string, any> = {};
        // Extract field values for this specific template
        // IMPORTANT: Include ALL fields from the template, even if they're undefined
        // This ensures required fields are always present (even if empty) for validation
        const templateFields = allFields[templateName] || [];
        templateFields.forEach(field => {
          // Always include the field, use empty string if undefined to pass validation
          const value = fieldValues[field.name];
          templateFieldValues[field.name] = value !== undefined && value !== null ? value : '';
        });
        
        // CRITICAL: Always ensure date fields are included, even if not in templateFields
        // These fields are required for document generation but might not be in the backend field list
        const today = new Date();
        // Always set date fields - use fieldValues if available, otherwise use current date
        if (!templateFieldValues['Dia'] || templateFieldValues['Dia'] === '') {
          templateFieldValues['Dia'] = fieldValues['Dia'] || today.getDate().toString();
        }
        if (!templateFieldValues['Mês'] || templateFieldValues['Mês'] === '') {
          templateFieldValues['Mês'] = fieldValues['Mês'] || (today.getMonth() + 1).toString();
        }
        if (!templateFieldValues['Ano'] || templateFieldValues['Ano'] === '') {
          templateFieldValues['Ano'] = fieldValues['Ano'] || today.getFullYear().toString();
        }

        const result = await apiService.generateVodafoneDocument(lead.id, templateName, templateFieldValues);
        newGeneratedDocs.push({
          id: result.document.id,
          lead_id: lead.id,
          user_id: 0,
          document_type: templateName,
          file_path: '',
          field_data: templateFieldValues,
          created_at: '',
          expires_at: '',
          is_expired: false,
          days_until_expiry: 7,
        });
      }
      
      setGeneratedDocs(newGeneratedDocs);
      setStep(4);
      
      // Show success message
      message.success(`${newGeneratedDocs.length} documento(s) guardado(s) com sucesso! Os documentos estão agora disponíveis na secção "Documentos".`);
      
      // Call onSave callback to refresh documents list
      if (onSave) {
        onSave();
      }
      
    } catch (e) {
      setError('Erro ao guardar documentos.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleDownload = async (docId: number, docType: string) => {
    setLoading(true);
    setLoadingMessage(`A descarregar documento: ${docType}...`);
    try {
      const blob = await apiService.downloadVodafoneDocument(docId);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docType}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (e) {
      setError('Erro ao descarregar o documento.');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <div>
          <Title level={4}>Selecione os modelos de documento Vodafone</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Pode selecionar múltiplos documentos para gerar em lote
          </Text>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Escolha um ou mais documentos"
            onChange={handleTemplateSelect}
            value={selectedTemplates}
            maxTagCount="responsive"
          >
            {templates.map((tpl) => (
              <Option key={tpl.name} value={tpl.name}>{tpl.display_name}</Option>
            ))}
          </Select>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={handlePreviousStep}>Anterior</Button>
            <Button type="primary" onClick={handleNextStep} disabled={selectedTemplates.length === 0}>
              Próximo
            </Button>
          </Space>
        </div>
      );
    }
    
    if (step === 1) {
      const currentTemplate = getCurrentTemplate();
      const currentFields = getCurrentFields();
      const currentTemplateObj = templates.find(t => t.name === currentTemplate);
      const currentPages = documentPages[currentTemplate] || [];
      const currentPageConfig = currentPages.find(p => p.page_number === currentPage) || currentPages[0];
      
      // Get fields for current page
      const pageFields = currentPageConfig ? 
        currentFields.filter(field => currentPageConfig.field_names.includes(field.name)) : 
        currentFields;
      
      const totalPages = currentPages.length;
      
      return (
        <Form layout="vertical">
          <Title level={4}>
            Documento {currentDocumentIndex + 1} de {selectedTemplates.length}: {currentTemplateObj?.display_name || currentTemplate}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Preencha os campos para o documento "{currentTemplateObj?.display_name || currentTemplate}"
          </Text>
          
          {/* Page Navigation */}
          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text strong>Página {currentPage} de {totalPages}: {currentPageConfig?.page_name}</Text>
            {currentPageConfig?.page_description && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{currentPageConfig.page_description}</Text>
              </div>
            )}
          </div>
          
          {/* Page Fields */}
          {(() => {
            // Custom rendering for PACOTE / SERVIÇOS CONTRATADOS (page 2)
            if (
              currentTemplate === 'Contrato de Adesão' &&
              currentPageConfig?.page_name === 'PACOTE / SERVIÇOS CONTRATADOS'
            ) {
              return (
                <>
                  {pageFields.map((field, idx) => (
                    <Form.Item
                      key={field.name}
                      label={field.label || field.name}
                      required={field.required}
                    >
                      <Input
                        value={fieldValues[field.name] || ''}
                        onChange={e => handleFieldChange(field.name, e.target.value)}
                        placeholder={`Introduza ${field.label || field.name}`}
                      />
                    </Form.Item>
                  ))}
                </>
              );
            }

            // Custom rendering for TARIFÁRIOS MÓVEIS (page 3)
            if (
              currentTemplate === 'Contrato de Adesão' &&
              currentPageConfig?.page_name === 'TARIFÁRIOS MÓVEIS'
            ) {
              // Define left and right columns
              const leftFields = [
                'Telemóvel 1',
                'Telemóvel 2',
                'Telemóvel 3',
                'Telemóvel 4',
                'Internet Móvel 1',
                'Internet Móvel 2',
              ];
              const rightFields = [
                'Tarifário 1',
                'Tarifário 2',
                'Tarifário 3',
                'Tarifário 4',
                'Tarifário Internet 1',
                'Tarifário Internet 2',
              ];
              return (
                <div style={{ display: 'flex', gap: 24 }}>
                  <div style={{ flex: 1 }}>
                    {leftFields.map(name => {
                      const field = pageFields.find(f => f.name === name);
                      if (!field) return null;
                      return (
                        <Form.Item
                          key={field.name}
                          label={field.label || field.name}
                          required={field.required}
                          style={{ marginBottom: 12 }}
                        >
                          <Input
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    })}
                  </div>
                  <div style={{ flex: 1 }}>
                    {rightFields.map(name => {
                      const field = pageFields.find(f => f.name === name);
                      if (!field) return null;
                      return (
                        <Form.Item
                          key={field.name}
                          label={field.label || field.name}
                          required={field.required}
                          style={{ marginBottom: 12 }}
                        >
                          <Input
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // Custom rendering for Formulario Desativação Apoiada, page 2
            if (
              currentTemplate === 'Formulario Desativação Apoiada' &&
              currentPageConfig?.page_number === 2
            ) {
              const otherFields = pageFields.filter(f => !['Cidade', 'dia', 'mes', 'ano'].includes(f.name));
              // Inline date/city fields
              const cidadeField = pageFields.find(f => f.name === 'Cidade');
              const diaField = pageFields.find(f => f.name === 'dia');
              const mesField = pageFields.find(f => f.name === 'mes');
              const anoField = pageFields.find(f => f.name === 'ano');
              return (
                <>
                  {/* Render non-date/city fields */}
                  {otherFields.map(field => {
                    if (field.name === 'Operadora' && field.type === '/Ch' && field.options) {
                      return (
                        <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                          <Select
                            value={fieldValues[field.name] || undefined}
                            onChange={value => handleFieldChange(field.name, value)}
                            placeholder={`Selecione ${field.label || field.name}`}
                            style={{ width: '100%' }}
                          >
                            {field.options.map((option: string, idx: number) => (
                              <Option key={idx} value={option}>{option}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }
                    return (
                      <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                        <Input
                          value={fieldValues[field.name] || ''}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={`Introduza ${field.label || field.name}`}
                        />
                      </Form.Item>
                    );
                  })}

                  {/* Inline Cidade, Dia, Mês, Ano */}
                  {(cidadeField || diaField || mesField || anoField) && (
                    <Form.Item style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {cidadeField && (
                          <Input
                            style={{ width: 120 }}
                            placeholder="Cidade"
                            value={fieldValues[cidadeField.name] || ''}
                            onChange={e => handleFieldChange(cidadeField.name, e.target.value)}
                          />
                        )}
                        {diaField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Dia"
                            value={fieldValues[diaField.name] || ''}
                            onChange={e => handleFieldChange(diaField.name, e.target.value)}
                          />
                        )}
                        {mesField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Mês"
                            value={fieldValues[mesField.name] || ''}
                            onChange={e => handleFieldChange(mesField.name, e.target.value)}
                          />
                        )}
                        {anoField && (
                          <Input
                            style={{ width: 80 }}
                            placeholder="Ano"
                            value={fieldValues[anoField.name] || ''}
                            onChange={e => handleFieldChange(anoField.name, e.target.value)}
                          />
                        )}
                      </div>
                    </Form.Item>
                  )}
                </>
              );
            }

            // Custom rendering for Formulario Desativação Apoiada, page 1
            if (
              currentTemplate === 'Formulario Desativação Apoiada' &&
              currentPageConfig?.page_number === 1
            ) {
              // Find fields
              const nomeCompletoField = pageFields.find(f => f.name === 'Nome Completo');
              const moradaCompletaField = pageFields.find(f => f.name === 'Morada Completa');
              const codigoPostal4Field = pageFields.find(f => f.name === 'Código Postal 4');
              const codigoPostal3Field = pageFields.find(f => f.name === 'Código Postal 3');
              return (
                <>
                  {/* Nome Completo as first field */}
                  {nomeCompletoField && (
                    <Form.Item key={nomeCompletoField.name} label={nomeCompletoField.label || nomeCompletoField.name} required={nomeCompletoField.required}>
                      <Input
                        value={fieldValues[nomeCompletoField.name] || ''}
                        onChange={e => handleFieldChange(nomeCompletoField.name, e.target.value)}
                        placeholder={`Introduza ${nomeCompletoField.label || nomeCompletoField.name}`}
                      />
                    </Form.Item>
                  )}
                  {/* Morada Completa */}
                  {moradaCompletaField && (
                    <Form.Item key={moradaCompletaField.name} label={moradaCompletaField.label || moradaCompletaField.name} required={moradaCompletaField.required}>
                      <Input
                        value={fieldValues[moradaCompletaField.name] || ''}
                        onChange={e => handleFieldChange(moradaCompletaField.name, e.target.value)}
                        placeholder={`Introduza ${moradaCompletaField.label || moradaCompletaField.name}`}
                      />
                    </Form.Item>
                  )}
                  {/* Código Postal as a single input group */}
                  {codigoPostal4Field && codigoPostal3Field && (
                    <Form.Item key="codigo_postal_group" label="Código Postal" required>
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <Input
                          value={fieldValues['Código Postal 4'] || ''}
                          onChange={e => handleFieldChange('Código Postal 4', e.target.value)}
                          placeholder="0000"
                          maxLength={4}
                          style={{ width: 80 }}
                        />
                        <span style={{ margin: '0 8px' }}>-</span>
                        <Input
                          value={fieldValues['Código Postal 3'] || ''}
                          onChange={e => handleFieldChange('Código Postal 3', e.target.value)}
                          placeholder="000"
                          maxLength={3}
                          style={{ width: 60 }}
                        />
                      </span>
                    </Form.Item>
                  )}
                </>
              );
            }

            // Custom rendering for Contrato de Adesão, page 6
            if (
              currentTemplate === 'Contrato de Adesão' &&
              currentPageConfig?.page_number === 6
            ) {
              // Find fields
              const diaField = pageFields.find(f => f.name === 'Dia');
              const mesField = pageFields.find(f => f.name === 'Mês');
              const anoField = pageFields.find(f => f.name === 'Ano');
              // Other fields
              const otherFields = pageFields.filter(f => !['Dia', 'Mês', 'Ano'].includes(f.name));
              return (
                <>
                  {/* Inline Dia, Mês, Ano */}
                  {(diaField || mesField || anoField) && (
                    <Form.Item label="Data" style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {diaField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Dia"
                            value={fieldValues[diaField.name] || ''}
                            onChange={e => handleFieldChange(diaField.name, e.target.value)}
                          />
                        )}
                        {mesField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Mês"
                            value={fieldValues[mesField.name] || ''}
                            onChange={e => handleFieldChange(mesField.name, e.target.value)}
                          />
                        )}
                        {anoField && (
                          <Input
                            style={{ width: 80 }}
                            placeholder="Ano"
                            value={fieldValues[anoField.name] || ''}
                            onChange={e => handleFieldChange(anoField.name, e.target.value)}
                          />
                        )}
                      </div>
                    </Form.Item>
                  )}
                  {/* Render other fields as usual */}
                  {otherFields.map(field => (
                    <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                      <Input
                        value={fieldValues[field.name] || ''}
                        onChange={e => handleFieldChange(field.name, e.target.value)}
                        placeholder={`Introduza ${field.label || field.name}`}
                      />
                    </Form.Item>
                  ))}
                </>
              );
            }

            // Custom rendering for Pedido de portabilidade e denúncia de contrato, page 1
            if (
              currentTemplate === 'Pedido de portabilidade e denúncia de contrato' &&
              currentPageConfig?.page_number === 1
            ) {
              const nomeCompletoField = pageFields.find(f => f.name === 'Nome Completo');
              const nifField = pageFields.find(f => f.name === 'NIF');
              return (
                <>
                  {nomeCompletoField && (
                    <Form.Item key={nomeCompletoField.name} label={nomeCompletoField.label || nomeCompletoField.name} required={nomeCompletoField.required}>
                      <Input
                        value={fieldValues[nomeCompletoField.name] || ''}
                        onChange={e => handleFieldChange(nomeCompletoField.name, e.target.value)}
                        placeholder={`Introduza ${nomeCompletoField.label || nomeCompletoField.name}`}
                      />
                    </Form.Item>
                  )}
                  {nifField && (
                    <Form.Item key={nifField.name} label={nifField.label || nifField.name} required={nifField.required}>
                      <Input
                        value={fieldValues[nifField.name] || ''}
                        onChange={e => handleFieldChange(nifField.name, e.target.value)}
                        placeholder={`Introduza ${nifField.label || nifField.name}`}
                      />
                    </Form.Item>
                  )}
                </>
              );
            }

            // Custom rendering for Pedido de portabilidade e denúncia de contrato, page 2
            if (
              currentTemplate === 'Pedido de portabilidade e denúncia de contrato' &&
              currentPageConfig?.page_number === 2
            ) {

              // Table rows
              const rows = [1, 2, 3, 4, 5];
              return (
                <>

                  <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                    <div style={{ display: 'flex', fontWeight: 500, marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>N.º de Telemóvel</div>
                      <div style={{ flex: 1 }}>Código de Validação de Portabilidade (CVP)</div>
                      <div style={{ flex: 1 }}>N.º Cartão SIM</div>
                    </div>
                    {rows.map(row => (
                      <div key={row} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <Input
                          style={{ flex: 1 }}
                          placeholder={`N de TelemóvelRow${row}`}
                          value={fieldValues[`N de TelemóvelRow${row}`] || ''}
                          onChange={e => handleFieldChange(`N de TelemóvelRow${row}`, e.target.value)}
                        />
                        <Input
                          style={{ flex: 1 }}
                          placeholder={`Código de Validação de Portabilidade CVPRow${row}`}
                          value={fieldValues[`Código de Validação de Portabilidade CVPRow${row}`] || ''}
                          onChange={e => handleFieldChange(`Código de Validação de Portabilidade CVPRow${row}`, e.target.value)}
                        />
                        <Input
                          style={{ flex: 1 }}
                          placeholder={`N Cartão SIMRow${row}`}
                          value={fieldValues[`N Cartão SIMRow${row}`] || ''}
                          onChange={e => handleFieldChange(`N Cartão SIMRow${row}`, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              );
            }

            // Custom rendering for Pedido de portabilidade e denúncia de contrato, page 3
            if (
              currentTemplate === 'Pedido de portabilidade e denúncia de contrato' &&
              currentPageConfig?.page_number === 3
            ) {
              const diaField = pageFields.find(f => f.name === 'Dia');
              const mesField = pageFields.find(f => f.name === 'Mês');
              const anoField = pageFields.find(f => f.name === 'Ano');
              const horaField = pageFields.find(f => f.name === 'Hora');
              const minutoField = pageFields.find(f => f.name === 'Minuto');
              const otherFields = pageFields.filter(f => !['Dia', 'Mês', 'Ano', 'Hora', 'Minuto'].includes(f.name));
              return (
                <>
                  {/* Inline Dia, Mês, Ano, Hora, Minuto */}
                  {(diaField || mesField || anoField || horaField || minutoField) && (
                    <Form.Item label="Data e Hora" style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {diaField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Dia"
                            value={fieldValues[diaField.name] || ''}
                            onChange={e => handleFieldChange(diaField.name, e.target.value)}
                          />
                        )}
                        {mesField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Mês"
                            value={fieldValues[mesField.name] || ''}
                            onChange={e => handleFieldChange(mesField.name, e.target.value)}
                          />
                        )}
                        {anoField && (
                          <Input
                            style={{ width: 80 }}
                            placeholder="Ano"
                            value={fieldValues[anoField.name] || ''}
                            onChange={e => handleFieldChange(anoField.name, e.target.value)}
                          />
                        )}
                        {horaField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Hora"
                            value={fieldValues[horaField.name] || ''}
                            onChange={e => handleFieldChange(horaField.name, e.target.value)}
                          />
                        )}
                        {minutoField && (
                          <Input
                            style={{ width: 60 }}
                            placeholder="Minuto"
                            value={fieldValues[minutoField.name] || ''}
                            onChange={e => handleFieldChange(minutoField.name, e.target.value)}
                          />
                        )}
                      </div>
                    </Form.Item>
                  )}
                  {/* Render other fields as usual */}
                  {otherFields.map(field => (
                    <Form.Item key={field.name} label={field.label || field.name} required={field.required}>
                      <Input
                        value={fieldValues[field.name] || ''}
                        onChange={e => handleFieldChange(field.name, e.target.value)}
                        placeholder={`Introduza ${field.label || field.name}`}
                      />
                    </Form.Item>
                  ))}
                </>
              );
            }

            // Custom rendering for Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes, page 1
            if (
              currentTemplate === 'Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes' &&
              currentPageConfig?.page_number === 1
            ) {
              const nomeCompletoField = pageFields.find(f => f.name === 'Nome Completo');
              const nifField = pageFields.find(f => f.name === 'NIF');
              const telemovelContactoField = pageFields.find(f => f.name === 'Telemóvel de Contacto');
              return (
                <>
                  {nomeCompletoField && (
                    <Form.Item key={nomeCompletoField.name} label={nomeCompletoField.label || nomeCompletoField.name} required={nomeCompletoField.required}>
                      <Input
                        value={fieldValues[nomeCompletoField.name] || ''}
                        onChange={e => handleFieldChange(nomeCompletoField.name, e.target.value)}
                        placeholder={`Introduza ${nomeCompletoField.label || nomeCompletoField.name}`}
                      />
                    </Form.Item>
                  )}
                  {nifField && (
                    <Form.Item key={nifField.name} label={nifField.label || nifField.name} required={nifField.required}>
                      <Input
                        value={fieldValues[nifField.name] || ''}
                        onChange={e => handleFieldChange(nifField.name, e.target.value)}
                        placeholder={`Introduza ${nifField.label || nifField.name}`}
                      />
                    </Form.Item>
                  )}
                  {telemovelContactoField && (
                    <Form.Item key={telemovelContactoField.name} label={telemovelContactoField.label || telemovelContactoField.name} required={telemovelContactoField.required}>
                      <Input
                        value={fieldValues[telemovelContactoField.name] || ''}
                        onChange={e => handleFieldChange(telemovelContactoField.name, e.target.value)}
                        placeholder={`Introduza ${telemovelContactoField.label || telemovelContactoField.name}`}
                      />
                    </Form.Item>
                  )}
                </>
              );
            }

            // Custom rendering for Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes, page 2
            if (
              currentTemplate === 'Pedido de Portabilidade, Denúncia de Contrato e Autorização para a realização de testes' &&
              currentPageConfig?.page_number === 2
            ) {
              // Fields arranged according to the correct specification:
              // 1. Telefone Fixo a Portar
              // 2. CVP
              // 3. Dia Mês Ano Hora Minuto
              // 4. SFID Nome/Assinatura Comercial
              
              const telefoneFixoField = pageFields.find(f => f.name === 'Telefone Fixo a Portar');
              const cvpField = pageFields.find(f => f.name === 'CVP');
              const diaField = pageFields.find(f => f.name === 'Dia');
              const mesField = pageFields.find(f => f.name === 'Mês');
              const anoField = pageFields.find(f => f.name === 'Ano');
              const horaField = pageFields.find(f => f.name === 'Hora');
              const minutoField = pageFields.find(f => f.name === 'Minuto');
              const sfidField = pageFields.find(f => f.name === 'SFID');
              const nomeAssinaturaField = pageFields.find(f => f.name === 'Nome  Assinatura Comercial');
              
              return (
                <>
                  {/* 1. Telefone Fixo a Portar */}
                  {telefoneFixoField && (
                    <Form.Item key={telefoneFixoField.name} label={telefoneFixoField.label || telefoneFixoField.name} required={telefoneFixoField.required}>
                      <Input
                        value={fieldValues[telefoneFixoField.name] || ''}
                        onChange={e => handleFieldChange(telefoneFixoField.name, e.target.value)}
                        placeholder={`Introduza ${telefoneFixoField.label || telefoneFixoField.name}`}
                      />
                    </Form.Item>
                  )}
                  
                  {/* 2. CVP */}
                  {cvpField && (
                    <Form.Item key={cvpField.name} label={cvpField.label || cvpField.name} required={cvpField.required}>
                      <Input
                        value={fieldValues[cvpField.name] || ''}
                        onChange={e => handleFieldChange(cvpField.name, e.target.value)}
                        placeholder={`Introduza ${cvpField.label || cvpField.name}`}
                      />
                    </Form.Item>
                  )}
                  
                  {/* 3. Dia Mês Ano Hora Minuto */}
                  <Form.Item label="Data e Hora" style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {[diaField, mesField, anoField, horaField, minutoField].map(field => {
                        if (!field) return null;
                        return (
                          <Input
                            key={field.name}
                            style={{ flex: 1 }}
                            placeholder={field.label || field.name}
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                          />
                        );
                      })}
                    </div>
                  </Form.Item>
                  
                  {/* 4. SFID Nome/Assinatura Comercial */}
                  <Form.Item label="Assinatura" style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {[sfidField, nomeAssinaturaField].map(field => {
                        if (!field) return null;
                        return (
                          <Input
                            key={field.name}
                            style={{ flex: 1 }}
                            placeholder={field.label || field.name}
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                          />
                        );
                      })}
                    </div>
                  </Form.Item>
                </>
              );
            }

            // Default rendering for all other pages
            return (
              <>
                {pageFields.map((field, idx) => {
                  // Hide default fields for Contrato de Adesão (these are filled automatically)
                  if (currentTemplate === 'Contrato de Adesão' && 
                      (field.name === '6im' || field.name === 'Não Condições de Privacidade' || (/^24#\d+$/.test(field.name) && field.name !== '24#0'))) {
                    return null;
                  }
                  // Special rendering for Código Postal (DADOS DO CLIENTE)
                  if (
                    currentTemplate === 'Contrato de Adesão' &&
                    currentPageConfig?.page_name === 'DADOS DO CLIENTE' &&
                    field.name === 'Código Postal 4'
                  ) {
                    return (
                      <Form.Item
                        key="codigo_postal_group"
                        label="Código Postal"
                        required
                      >
                        <Space>
                          <Input
                            value={fieldValues['Código Postal 4'] || ''}
                            onChange={e => handleFieldChange('Código Postal 4', e.target.value)}
                            placeholder="0000"
                            maxLength={4}
                            style={{ width: 80 }}
                          />
                          <Text style={{ marginLeft: 4, marginRight: 4 }}>-</Text>
                          <Input
                            value={fieldValues['Código Postal 3'] || ''}
                            onChange={e => handleFieldChange('Código Postal 3', e.target.value)}
                            placeholder="000"
                            maxLength={3}
                            style={{ width: 60 }}
                          />
                        </Space>
                      </Form.Item>
                    );
                  }
                  // Skip Código Postal 3 separately on DADOS DO CLIENTE
                  if (
                    currentTemplate === 'Contrato de Adesão' &&
                    currentPageConfig?.page_name === 'DADOS DO CLIENTE' &&
                    field.name === 'Código Postal 3'
                  ) {
                    return null;
                  }
                  // Handle Nome Completo fields for Contrato de Adesão
                  if (field.name === 'Nome Completo') {
                    if (
                      currentTemplate === 'Contrato de Adesão' &&
                      currentPageConfig?.page_name === 'DADOS DO CLIENTE'
                    ) {
                      // Page 1: Use the original field name
                      return (
                        <Form.Item
                          key={field.name}
                          label={field.label || field.name}
                          required={field.required}
                        >
                          <Input
                            value={fieldValues[field.name] || ''}
                            onChange={e => handleFieldChange(field.name, e.target.value)}
                            placeholder={`Introduza ${field.label || field.name}`}
                          />
                        </Form.Item>
                      );
                    } else if (
                      currentTemplate === 'Contrato de Adesão' &&
                      currentPageConfig?.page_name === 'AUTORIZAÇÃO DE PAGAMENTO POR DÉBITO DIRETO'
                    ) {
                      // Page 5: Use a different field name to make it independent
                      const page5FieldName = 'Nome Completo Autorização';
                      return (
                        <Form.Item
                          key={page5FieldName}
                          label="Nome Completo (Autorização)"
                          required={field.required}
                        >
                          <Input
                            value={fieldValues[page5FieldName] || ''}
                            onChange={e => handleFieldChange(page5FieldName, e.target.value)}
                            placeholder="Introduza Nome Completo para Autorização"
                          />
                        </Form.Item>
                      );
                    } else {
                      return null;
                    }
                  }
                  const isDropdown = field.type === '/Ch' && field.options && field.options.length > 0;
                  const validationError = getFieldValidationError(field, fieldValues[field.name]);
                  return (
                    <Form.Item
                      key={field.name}
                      label={field.label || field.name}
                      required={field.required}
                      validateStatus={validationError ? 'error' : undefined}
                      help={validationError}
                    >
                      {isDropdown ? (
                        <Select
                          value={fieldValues[field.name] || undefined}
                          onChange={value => handleFieldChange(field.name, value)}
                          placeholder={`Selecione ${field.label || field.name}`}
                          style={{ width: '100%' }}
                        >
                          {field.options?.map((option: string, index: number) => (
                            <Option key={index} value={option}>
                              {option}
                            </Option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          value={fieldValues[field.name] || ''}
                          onChange={e => handleFieldChange(field.name, e.target.value)}
                          placeholder={`Introduza ${field.label || field.name}`}
                        />
                      )}
                    </Form.Item>
                  );
                })}
              </>
            );
          })()}
          
          {/* Page Navigation Buttons */}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={handlePreviousStep}>
              {currentDocumentIndex === 0 ? 'Voltar' : 'Anterior'}
            </Button>
            
            <Button 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Página Anterior
            </Button>
            <Button 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Próxima Página
            </Button>
            
            <Button type="primary" onClick={handleNextDocument} loading={loading} disabled={loading}>
              {loading ? (currentDocumentIndex === selectedTemplates.length - 1 ? 'A concluir...' : 'A processar...') : (currentDocumentIndex === selectedTemplates.length - 1 ? 'Concluir' : 'Próximo')}
            </Button>
          </Space>
        </Form>
      );
    }
    
    if (step === 2) {
      return (
        <div>
          <Title level={4}>Revisão dos Documentos</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Revise os documentos selecionados antes de gerar
          </Text>
          
          {selectedTemplates.map((templateName, index) => {
            const templateObj = templates.find(t => t.name === templateName);
            const templateFields = allFields[templateName] || [];
            const filledFields = templateFields.filter(field => {
              const value = fieldValues[field.name];
              return value !== undefined && value !== null && value !== '';
            });
            
            return (
              <Card key={templateName} style={{ marginBottom: 16 }}>
                <Title level={5}>Documento {index + 1}: {templateObj?.display_name || templateName}</Title>
                <Text type="secondary">
                  {filledFields.length} de {templateFields.length} campos preenchidos
                </Text>
                {filledFields.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>Campos preenchidos:</Text>
                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                      {filledFields.slice(0, 5).map(field => (
                        <li key={field.name}>
                          <Text type="secondary">
                            {field.label || field.name}: {fieldValues[field.name]}
                          </Text>
                        </li>
                      ))}
                      {filledFields.length > 5 && (
                        <li>
                          <Text type="secondary">... e mais {filledFields.length - 5} campos</Text>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </Card>
            );
          })}
          
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(1)}>Voltar</Button>
            <Button type="primary" onClick={handlePreview} disabled={loading} loading={loading}>
              {loading ? 'A gerar pré-visualização...' : 'Pré-visualizar'}
            </Button>
          </Space>
        </div>
      );
    }
    
    if (step === 3) {
      return (
        <div>
          <Title level={4}>Pré-visualização do PDF</Title>

          {Object.keys(previewData).length === 0 ? (
            <Spin />
          ) : (
            <div>
              {Object.entries(previewData).map(([templateName, pdfBase64]) => (
                <div key={templateName} style={{ marginBottom: 20 }}>
                  <Title level={5}>{templates.find(t => t.name === templateName)?.display_name || templateName}</Title>
                  <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}>
                    <iframe
                      src={`data:application/pdf;base64,${pdfBase64}`}
                      title={`Pré-visualização PDF - ${templateName}`}
                      width="100%"
                      height="500px"
                      style={{ border: 'none' }}
                    />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Button 
                      type="link" 
                      onClick={() => {
                        const blob = new Blob([Uint8Array.from(atob(pdfBase64), c => c.charCodeAt(0))], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${templateName}.pdf`;
                        link.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Descarregar PDF para verificar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(2)}>Voltar</Button>
            <Button type="primary" onClick={handleGenerate} disabled={loading} loading={loading}>
              {loading ? 'A guardar documentos...' : 'Guardar Documentos'}
            </Button>
          </Space>
        </div>
      );
    }
    
    if (step === 4 && generatedDocs.length > 0) {
      return (
        <div>
          <Alert
            message={`${generatedDocs.length} documento(s) guardado(s) com sucesso!`}
            description="Os documentos foram guardados e estão agora disponíveis na secção 'Documentos' para este lead. Pode descarregar os documentos ou fechar este assistente."
            type="success"
            showIcon
          />
          
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Documentos Guardados:</Title>
            {generatedDocs.map((doc, index) => {
              const templateObj = templates.find(t => t.name === doc.document_type);
              return (
                <Card key={doc.id} style={{ marginBottom: 8 }}>
                  <Space>
                    <Text>{index + 1}. {templateObj?.display_name || doc.document_type}</Text>
                    <Button 
                      type="primary" 
                      size="small" 
                      icon={<DownloadOutlined />} 
                      onClick={() => handleDownload(doc.id, doc.document_type)}
                      loading={loading}
                      disabled={loading}
                    >
                      {loading ? 'A descarregar...' : 'Descarregar'}
                    </Button>
                  </Space>
                </Card>
              );
            })}
          </div>
          
          <Space style={{ marginTop: 16 }}>
            <Button onClick={onClose} icon={<CloseOutlined />}>Fechar</Button>
          </Space>
        </div>
      );
    }
    
    return null;
  };

  const getSteps = () => {
    const steps = [
      { title: 'Seleção' },
      { title: 'Campos' },
      { title: 'Revisão' },
      { title: 'Pré-visualização' },
      { title: 'Guardado' }
    ];
    return steps;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Guardar Documentos Vodafone"
      width={700}
      destroyOnClose
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Steps current={step} style={{ marginBottom: 24 }}>
        {getSteps().map((stepItem, index) => (
          <Step key={index} title={stepItem.title} />
        ))}
      </Steps>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          {loadingMessage && (
            <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
              {loadingMessage}
            </div>
          )}
        </div>
      ) : renderStepContent()}
    </Modal>
  );
};

export default VodafoneDocumentWizard; 